export type TierGrade = 'S' | 'A+' | 'A' | 'B' | 'C' | 'EX';

export type AnimeSeason = 'WINTER' | 'SPRING' | 'SUMMER' | 'FALL';

export type BroadcastDay =
  | 'MON'
  | 'TUE'
  | 'WED'
  | 'THU'
  | 'FRI'
  | 'SAT'
  | 'SUN'
  | 'UNKNOWN';

export type PlatformType =
  | 'youtube'
  | 'anime-waku'
  | 'animeruka'
  | 'bilibili'
  | 'wetv'
  | 'netflix'
  | 'iqiyi'
  | 'crunchyroll'
  | 'other';

export interface AnimeSource {
  type: PlatformType;
  name: string; // เช่น "YouTube (Muse Thailand)", "Anime-Waku", "Bilibili", "WeTV"
  url: string;
  latestEp: number;
  audio?: 'ซับไทย' | 'พากย์ไทย'; // audio track this link serves (sub and dub pages are separate)
}

export interface AnimeEpisode {
  number: number;
  title: string;
  url: string;
  audio: 'ซับไทย' | 'พากย์ไทย';
  thumbnail?: string;
  vipBadge?: string; // เช่น "VIP", "พรีเมียม", "Express" หรือ "" (ดูฟรี)
  publishTime?: string;
}

export interface Anime {
  id: string;
  title: string;
  thaiTitle?: string;
  grade: TierGrade;
  season: AnimeSeason;
  year: number;
  watchedEp: number;
  latestEp: number;
  totalEp?: number; // จำนวนตอนทั้งหมด เช่น 12, 24 (0 หรือไม่ระบุ = ยังไม่ทราบ)
  imageUrl: string;
  status: 'กำลังฉาย' | 'จบแล้ว' | 'รอฉาย';
  audioType: 'ซับไทย' | 'พากย์ไทย' | 'ซับ+พากย์';
  broadcastDay: BroadcastDay;
  broadcastTime?: string; // เช่น "22:00", "20:30" หรือ "" ถ้าไม่รู้เวลาแน่ชัด
  // day/time follow this site's schedule (other sources don't overwrite them). 'animeruka' is
  // dropped again while the show is off AnimeRuka's schedule; 'anione' is pinned by hand
  scheduleSource?: 'animeruka' | 'anione';
  // Listed under AnimeRuka's "สัปดาห์นี้งดฉาย / พักฉาย": label as shown ("กลับมา 8 พ.ย."),
  // returnOn = YYYY-MM-DD ('' when the site gives no date). No airtime alerts until then.
  onBreak?: { label: string; returnOn: string };
  notifyLine: boolean; // เปิดแจ้งเตือน LINE สำหรับเรื่องนี้หรือไม่
  lastNotifiedEp?: number; // last episode a LINE alert went out for (set silently the first time)
  lastAirtimeAlertOn?: string; // YYYY-MM-DD of the last "ถึงเวลาฉายแล้ว" alert
  lastUpdatedAt?: string; // YYYY-MM-DD
  finishedAt?: string; // YYYY-MM-DD — the day the show ended (drives the 3-week "เพิ่งจบ" window)
  linksCheckedAt?: number; // ms — last full-catalogue search for this show's other platform links
  detectedDayNote?: string; // บันทึกช่วยจำ เช่น "ตรวจพบตอนใหม่ล่าสุดเมื่อวันเสาร์"
  synopsis?: string;
  score?: string;
  malTitle?: string; // ชื่อเรื่องบน MyAnimeList ที่เลือกเอง (ใช้ค้นคะแนนแทน title)
  order: number;
  sources: AnimeSource[];
  episodes?: AnimeEpisode[];
}

export interface SeasonConfig {
  id: AnimeSeason;
  nameTh: string;
  nameEn: string;
  startMonthTh: string;
  monthsRangeTh: string;
  icon: string;
  gradient: string;
  badgeClass: string;
  glowClass: string;
  borderClass: string;
  accentText: string;
  description: string;
}

export const SEASON_CONFIGS: Record<AnimeSeason, SeasonConfig> = {
  WINTER: {
    id: 'WINTER',
    nameTh: 'ฤดูหนาว',
    nameEn: 'Winter',
    startMonthTh: 'เริ่ม ม.ค.',
    monthsRangeTh: 'ม.ค. – มี.ค.',
    icon: '❄️',
    gradient: 'from-cyan-500 via-sky-500 to-blue-600',
    badgeClass: 'bg-cyan-500/20 text-cyan-200 border-cyan-400/40',
    glowClass: 'shadow-cyan-500/20',
    borderClass: 'border-cyan-500/40 hover:border-cyan-400/70',
    accentText: 'text-cyan-300',
    description: 'อนิเมะต้นปี ฤดูหนาว (มกราคม – มีนาคม)',
  },
  SPRING: {
    id: 'SPRING',
    nameTh: 'ฤดูใบไม้ผลิ',
    nameEn: 'Spring',
    startMonthTh: 'เริ่ม เม.ย.',
    monthsRangeTh: 'เม.ย. – มิ.ย.',
    icon: '🌸',
    gradient: 'from-pink-500 via-rose-500 to-fuchsia-600',
    badgeClass: 'bg-pink-500/20 text-pink-200 border-pink-400/40',
    glowClass: 'shadow-pink-500/20',
    borderClass: 'border-pink-500/40 hover:border-pink-400/70',
    accentText: 'text-pink-300',
    description: 'อนิเมะซากุระบาน ฤดูใบไม้ผลิ (เมษายน – มิถุนายน)',
  },
  SUMMER: {
    id: 'SUMMER',
    nameTh: 'ฤดูร้อน',
    nameEn: 'Summer',
    startMonthTh: 'เริ่ม ก.ค.',
    monthsRangeTh: 'ก.ค. – ก.ย.',
    icon: '☀️',
    gradient: 'from-amber-400 via-orange-500 to-rose-500',
    badgeClass: 'bg-amber-500/20 text-amber-200 border-amber-400/40',
    glowClass: 'shadow-amber-500/20',
    borderClass: 'border-amber-500/40 hover:border-amber-400/70',
    accentText: 'text-amber-300',
    description: 'อนิเมะกลางปีสุดเดือด ฤดูร้อน (กรกฎาคม – กันยายน)',
  },
  FALL: {
    id: 'FALL',
    nameTh: 'ฤดูใบไม้ร่วง',
    nameEn: 'Fall / Autumn',
    startMonthTh: 'เริ่ม ต.ค.',
    monthsRangeTh: 'ต.ค. – ธ.ค.',
    icon: '🍁',
    gradient: 'from-orange-600 via-rose-600 to-purple-700',
    badgeClass: 'bg-orange-500/20 text-orange-200 border-orange-400/40',
    glowClass: 'shadow-orange-500/20',
    borderClass: 'border-orange-500/40 hover:border-orange-400/70',
    accentText: 'text-orange-300',
    description: 'อนิเมะส่งท้ายปีฟอร์มยักษ์ ฤดูใบไม้ร่วง (ตุลาคม – ธันวาคม)',
  },
};

export interface DayConfig {
  id: BroadcastDay;
  nameTh: string;
  shortTh: string;
  nameEn: string;
  jsDayIndex: number; // 0 = Sun, 1 = Mon, ..., -1 = Unknown
  colorClass: string;
  badgeBg: string;
  headerGradient: string;
}

export const BROADCAST_DAYS: DayConfig[] = [
  {
    id: 'MON',
    nameTh: 'วันจันทร์',
    shortTh: 'จ.',
    nameEn: 'Monday',
    jsDayIndex: 1,
    colorClass: 'text-yellow-300',
    badgeBg: 'bg-yellow-500/20 text-yellow-200 border-yellow-400/40',
    headerGradient: 'from-yellow-500/30 to-amber-600/10 border-yellow-500/40',
  },
  {
    id: 'TUE',
    nameTh: 'วันอังคาร',
    shortTh: 'อ.',
    nameEn: 'Tuesday',
    jsDayIndex: 2,
    colorClass: 'text-pink-300',
    badgeBg: 'bg-pink-500/20 text-pink-200 border-pink-400/40',
    headerGradient: 'from-pink-500/30 to-rose-600/10 border-pink-500/40',
  },
  {
    id: 'WED',
    nameTh: 'วันพุธ',
    shortTh: 'พ.',
    nameEn: 'Wednesday',
    jsDayIndex: 3,
    colorClass: 'text-emerald-300',
    badgeBg: 'bg-emerald-500/20 text-emerald-200 border-emerald-400/40',
    headerGradient: 'from-emerald-500/30 to-teal-600/10 border-emerald-500/40',
  },
  {
    id: 'THU',
    nameTh: 'วันพฤหัสบดี',
    shortTh: 'พฤ.',
    nameEn: 'Thursday',
    jsDayIndex: 4,
    colorClass: 'text-orange-300',
    badgeBg: 'bg-orange-500/20 text-orange-200 border-orange-400/40',
    headerGradient: 'from-orange-500/30 to-amber-600/10 border-orange-500/40',
  },
  {
    id: 'FRI',
    nameTh: 'วันศุกร์',
    shortTh: 'ศ.',
    nameEn: 'Friday',
    jsDayIndex: 5,
    colorClass: 'text-sky-300',
    badgeBg: 'bg-sky-500/20 text-sky-200 border-sky-400/40',
    headerGradient: 'from-sky-500/30 to-blue-600/10 border-sky-500/40',
  },
  {
    id: 'SAT',
    nameTh: 'วันเสาร์',
    shortTh: 'ส.',
    nameEn: 'Saturday',
    jsDayIndex: 6,
    colorClass: 'text-purple-300',
    badgeBg: 'bg-purple-500/20 text-purple-200 border-purple-400/40',
    headerGradient: 'from-purple-500/30 to-indigo-600/10 border-purple-500/40',
  },
  {
    id: 'SUN',
    nameTh: 'วันอาทิตย์',
    shortTh: 'อา.',
    nameEn: 'Sunday',
    jsDayIndex: 0,
    colorClass: 'text-rose-300',
    badgeBg: 'bg-rose-500/20 text-rose-200 border-rose-400/40',
    headerGradient: 'from-rose-500/30 to-red-600/10 border-rose-500/40',
  },
  {
    id: 'UNKNOWN',
    nameTh: 'ยังไม่รู้วันแน่ชัด (รอเช็คเว็บ)',
    shortTh: 'รอเช็คเว็บ',
    nameEn: 'Web Watch / Unscheduled',
    jsDayIndex: -1,
    colorClass: 'text-teal-300',
    badgeBg: 'bg-teal-500/20 text-teal-200 border-teal-400/40',
    headerGradient: 'from-teal-500/30 to-cyan-600/10 border-teal-500/40',
  },
];

export interface TierInfo {
  grade: TierGrade;
  title: string;
  subtitle: string;
  gradient: string;
  headerBg: string;
  borderClass: string;
  badgeBg: string;
  textAccent: string;
}

export const TIER_CONFIGS: Record<TierGrade, TierInfo> = {
  S: {
    grade: 'S',
    title: 'S TIER',
    subtitle: 'ระดับตำนาน / ขึ้นหิ้ง',
    gradient: 'from-rose-600 via-red-600 to-rose-700',
    headerBg: 'bg-rose-950/40',
    borderClass: 'border-rose-500/40 hover:border-rose-500/60',
    badgeBg: 'bg-rose-600/30 text-rose-300 border-rose-500/40',
    textAccent: 'text-rose-400',
  },
  'A+': {
    grade: 'A+',
    title: 'A+ TIER',
    subtitle: 'ยอดเยี่ยม / ห้ามพลาด',
    gradient: 'from-pink-600 via-rose-500 to-orange-500',
    headerBg: 'bg-pink-950/40',
    borderClass: 'border-pink-500/40 hover:border-pink-500/60',
    badgeBg: 'bg-pink-600/30 text-pink-300 border-pink-500/40',
    textAccent: 'text-pink-400',
  },
  A: {
    grade: 'A',
    title: 'A TIER',
    subtitle: 'สนุกมาก / ติดตามทุกสัปดาห์',
    gradient: 'from-orange-500 via-amber-600 to-orange-600',
    headerBg: 'bg-orange-950/40',
    borderClass: 'border-orange-500/40 hover:border-orange-500/60',
    badgeBg: 'bg-orange-600/30 text-orange-300 border-orange-500/40',
    textAccent: 'text-orange-400',
  },
  B: {
    grade: 'B',
    title: 'B TIER',
    subtitle: 'ดูเพลิน / สนุกดี',
    gradient: 'from-amber-400 via-yellow-500 to-amber-500',
    headerBg: 'bg-yellow-950/40',
    borderClass: 'border-amber-500/40 hover:border-amber-500/60',
    badgeBg: 'bg-amber-600/30 text-amber-200 border-amber-500/40',
    textAccent: 'text-amber-400',
  },
  C: {
    grade: 'C',
    title: 'C TIER',
    subtitle: 'รอดอง / ดูยามว่าง',
    gradient: 'from-emerald-500 via-green-600 to-teal-700',
    headerBg: 'bg-emerald-950/40',
    borderClass: 'border-emerald-500/40 hover:border-emerald-500/60',
    badgeBg: 'bg-emerald-600/30 text-emerald-300 border-emerald-500/40',
    textAccent: 'text-emerald-400',
  },
  EX: {
    grade: 'EX',
    title: 'EX TIER',
    subtitle: 'หมวดพิเศษ / รอฉาย / เดอะมูฟวี่',
    gradient: 'from-fuchsia-600 via-purple-600 to-indigo-700',
    headerBg: 'bg-purple-950/40',
    borderClass: 'border-purple-500/40 hover:border-purple-500/60',
    badgeBg: 'bg-purple-600/30 text-purple-300 border-purple-500/40',
    textAccent: 'text-purple-400',
  },
};

export interface LineNotifyConfig {
  enabled: boolean;
  mode: 'line_messaging' | 'webhook';
  channelAccessToken: string;
  targetId: string;
  webhookUrl: string;
  notifyOnSchedule: boolean;
  notifyOnNewEpFound: boolean;
  autoCheckMinutes: number; // 0 = off, 15, 30, 60
  serverAlerts?: boolean; // settings are on the server, which sends the alerts every 10 min (site open or not)
}

export interface WakuFeedItem {
  url: string;
  imageUrl: string;
  season: AnimeSeason;
  year: number;
  seasonUnknown?: boolean; // catalogue release: season/year is only when the platform uploaded it
  fullTitle: string;
  displayTitle: string;
  romajiTitle: string;
  thaiTitle: string;
  latestEp: number;
  totalEp?: number;
  status: 'กำลังฉาย' | 'จบแล้ว';
  audioType: 'ซับไทย' | 'พากย์ไทย' | 'ซับ+พากย์';
  rating?: string;
  platform?: PlatformType;
  broadcastDay?: BroadcastDay;
  broadcastTime?: string;
  vipBadge?: string;
  synopsis?: string;
}
